import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Plus, 
  Filter, 
  Search, 
  Edit, 
  Trash2, 
  Calculator,
  X,
  Save,
  ArrowLeft,
  CheckSquare, 
  Square,
  Settings
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { useAppStore } from '../store';
import { formatCurrency } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import ErrorModal from '../components/modals/ErrorModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import { supabase } from '../lib/supabase';

// Define types
interface QuoteInputType {
  id: string;
  name: string;
  created_at: string;
}

interface QuoteInput {
  id: string;
  input_type_id: string;
  input_name: string;
  supplier: string;
  internal_cost: number;
  margin: number;
  final_price: number;
  yield: number | null;
  price_per_meter: number | null;
  is_active: boolean;
  quote_input_types?: {
    name: string;
  };
}

// Define new input form state
interface FormState {
  input_type_id: string;
  input_name: string;
  supplier: string;
  internal_cost: number;
  margin: number;
  yield: number | null;
  is_active: boolean;
}

const QuoteInputs: React.FC = () => {
  const { isAdmin, userRole, user } = useAuth();
  const navigate = useNavigate();
  const { toast, ToastComponent } = useToast();
  
  // Redirect if not admin
  useEffect(() => {
    if (userRole && userRole !== 'Administrador') {
      navigate('/');
    }
  }, [userRole, navigate]);

  // State for quote input types
  const [inputTypes, setInputTypes] = useState<QuoteInputType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  
  // State for quote inputs
  const [quoteInputs, setQuoteInputs] = useState<QuoteInput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // State for selected payment method
  const [selectedMethod, setSelectedMethod] = useState<string>('all');
  
  // Filters
  const [inputTypeFilter, setInputTypeFilter] = useState<string>('all');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Edit mode
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  
  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  
  // New input type modal
  const [isAddingInputType, setIsAddingInputType] = useState(false);
  const [newInputTypeName, setNewInputTypeName] = useState('');
  
  // Edit input types modal
  const [isEditingInputTypes, setIsEditingInputTypes] = useState(false);
  const [editingInputTypeName, setEditingInputTypeName] = useState('');
  const [editingInputTypeId, setEditingInputTypeId] = useState<string | null>(null);
  const [inputTypeToDelete, setInputTypeToDelete] = useState<string | null>(null);
  const [isDeleteInputTypeModalOpen, setIsDeleteInputTypeModalOpen] = useState(false);
  
  // Form state for new/edit item
  const [formState, setFormState] = useState<FormState>({
    input_type_id: '',
    input_name: '',
    supplier: '',
    internal_cost: 0,
    margin: 0,
    yield: null,
    is_active: true
  });
  
  // Fetch quote input types
  const fetchQuoteInputTypes = async () => {
    try {
      setIsLoadingTypes(true);
      
      const { data, error } = await supabase
        .from('quote_input_types')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      setInputTypes(data || []);
    } catch (error: any) {
      console.error('Error fetching quote input types:', error);
      setError(error.message || 'Error al cargar los tipos de insumos');
      setShowErrorModal(true);
    } finally {
      setIsLoadingTypes(false);
    }
  };
  
  // Fetch quote inputs
  const fetchQuoteInputs = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('quote_inputs')
        .select('*, quote_input_types(name)')
        .order('input_name');
      
      if (error) throw error;
      
      setQuoteInputs(data || []);
    } catch (error: any) {
      console.error('Error fetching quote inputs:', error);
      setError(error.message || 'Error al cargar los insumos');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch data on component mount
  useEffect(() => {
    fetchQuoteInputTypes();
    fetchQuoteInputs();
  }, []);
  
  // Get unique values for filters
  const uniqueInputTypes = useMemo(() => 
    [...new Set(quoteInputs.map(item => item.quote_input_types?.name || 'unknown'))],
    [quoteInputs]
  );

  const suppliers = useMemo(() => 
    [...new Set(quoteInputs.map(item => item.supplier))],
    [quoteInputs]
  );

  // Calculate product type totals
  const inputTypeTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    quoteInputs.forEach(input => {
      const typeName = input.quote_input_types ? input.quote_input_types.name : 'unknown';
      if (!totals[typeName]) {
        totals[typeName] = 0;
      }
      totals[typeName]++;
    });
    return totals;
  }, [quoteInputs]);

  // Filter and sort inputs
  const filteredInputs = useMemo(() => {
    return quoteInputs
      .filter(input => 
        (inputTypeFilter === 'all' || (input.quote_input_types?.name === inputTypeFilter)) &&
        (supplierFilter === 'all' || input.supplier === supplierFilter) &&
        (showInactive || input.is_active) &&
        (searchQuery === '' || 
          input.input_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          input.supplier.toLowerCase().includes(searchQuery.toLowerCase()))
      );
  }, [quoteInputs, inputTypeFilter, supplierFilter, showInactive, searchQuery]);

  // Get input type name by ID
  const getInputTypeName = (id: string): string => {
    const type = inputTypes.find(t => t.id === id);
    return type ? type.name : '';
  };
  
  // Check if input type is fabric
  const isTelaType = (id: string): boolean => {
    const type = inputTypes.find(t => t.id === id);
    const typeName = type ? type.name.toLowerCase() : '';
    return typeName === 'tela';
  };
  
  // Handle input change
  const handleInputChange = (field: keyof FormState, value: any) => {
    setFormState(prev => {
      const newState = { ...prev, [field]: value };
      return newState;
    });
  };
  
  // Reset form state
  const resetForm = () => {
    // Default to first input type if available
    const defaultTypeId = inputTypes.length > 0 ? inputTypes[0].id : '';
    
    setFormState({
      input_type_id: defaultTypeId,
      input_name: '',
      supplier: '',
      internal_cost: 0,
      margin: 0,
      yield: null,
      is_active: true
    });
  };
  
  // Start adding new item
  const handleAddNew = () => {
    resetForm();
    setIsAddingNew(true);
    setEditingId(null);
  };
  
  // Start editing item
  const handleEdit = (item: QuoteInput) => {
    setFormState({
      input_type_id: item.input_type_id || '',
      input_name: item.input_name,
      supplier: item.supplier,
      internal_cost: item.internal_cost,
      // Convert margin from decimal to percentage for display
      margin: item.margin * 100,
      yield: item.yield,
      is_active: item.is_active
    });
    setEditingId(item.id);
    setIsAddingNew(false);
  };
  
  // Save new or edited item
  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formState.input_type_id) {
        setError('El tipo de insumo es obligatorio');
        setShowErrorModal(true);
        return;
      }
      
      if (!formState.input_name.trim()) {
        setError('El nombre del insumo es obligatorio');
        setShowErrorModal(true);
        return;
      }
      
      if (!formState.supplier.trim()) {
        setError('El proveedor es obligatorio');
        setShowErrorModal(true);
        return;
      }
      
      // Convert margin from percentage to decimal for storage
      const marginDecimal = formState.margin / 100;
      
      const isTela = isTelaType(formState.input_type_id);
      
      // Calculate final_price based on internal_cost and margin
      const finalPrice = formState.internal_cost * (1 + marginDecimal);
      
      // Calculate price_per_meter for Tela type
      const pricePerMeter = isTela && formState.yield 
        ? finalPrice / formState.yield 
        : null;
      
      const saveData = {
        ...formState,
        margin: marginDecimal,
        price_per_meter: pricePerMeter,
        final_price: finalPrice
      };
      
      if (isAddingNew) {
        // Create new item
        const { data, error } = await supabase
          .from('quote_inputs')
          .insert([saveData])
          .select();
        
        if (error) throw error;
        
        // Log activity
        await supabase
          .from('activity_logs')
          .insert([{
            user_id: user?.id,
            action: 'Creación de insumo para cotización',
            entity_type: 'quote_input',
            entity_id: data[0].id,
            details: `Insumo creado: ${formState.input_name}`,
            action_type: 'create'
          }]);
        
        toast.success('Insumo creado', 'El insumo ha sido creado correctamente');
      } else if (editingId) {
        // Update existing item
        const { error } = await supabase
          .from('quote_inputs')
          .update(saveData)
          .eq('id', editingId);
        
        if (error) throw error;
        
        // Log activity
        await supabase
          .from('activity_logs')
          .insert([{
            user_id: user?.id,
            action: 'Actualización de insumo para cotización',
            entity_type: 'quote_input',
            entity_id: editingId,
            details: `Insumo actualizado: ${formState.input_name}`,
            action_type: 'update'
          }]);
        
        toast.success('Insumo actualizado', 'El insumo ha sido actualizado correctamente');
      }
      
      // Reset form and refresh data
      resetForm();
      setIsAddingNew(false);
      setEditingId(null);
      fetchQuoteInputs();
    } catch (error: any) {
      console.error('Error saving quote input:', error);
      setError(error.message || 'Error al guardar el insumo');
      setShowErrorModal(true);
    }
  };
  
  // Cancel editing or adding
  const handleCancel = () => {
    resetForm();
    setIsAddingNew(false);
    setEditingId(null);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      // Log activity before deleting
      const itemToDeleteData = quoteInputs.find(item => item.id === itemToDelete);
      
      if (itemToDeleteData) {
        await supabase
          .from('activity_logs')
          .insert([{
            user_id: user?.id,
            action: 'Eliminación de insumo para cotización',
            entity_type: 'quote_input',
            entity_id: itemToDelete,
            details: `Insumo eliminado: ${itemToDeleteData.input_name}`,
            action_type: 'delete'
          }]);
      }
      
      const { error } = await supabase
        .from('quote_inputs')
        .delete()
        .eq('id', itemToDelete);
      
      if (error) throw error;
      
      toast.success('Insumo eliminado', 'El insumo ha sido eliminado correctamente');
      
      // Refresh data
      fetchQuoteInputs();
    } catch (error: any) {
      console.error('Error deleting quote input:', error);
      setError(error.message || 'Error al eliminar el insumo');
      setShowErrorModal(true);
    } finally {
      setItemToDelete(null);
      setDeleteConfirmOpen(false);
    }
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setInputTypeFilter('all');
    setSupplierFilter('all');
    setSearchQuery('');
  };
  
  // Toggle active status
  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('quote_inputs')
        .update({ is_active: !currentStatus })
        .eq('id', id);
      
      if (error) throw error;
      
      // Log activity
      const item = quoteInputs.find(item => item.id === id);
      if (item) {
        await supabase
          .from('activity_logs')
          .insert([{
            user_id: user?.id,
            action: currentStatus ? 'Desactivación de insumo para cotización' : 'Activación de insumo para cotización',
            entity_type: 'quote_input',
            entity_id: id,
            details: `Insumo ${currentStatus ? 'desactivado' : 'activado'}: ${item.input_name}`,
            action_type: 'update'
          }]);
      }
      
      toast.success(
        currentStatus ? 'Insumo desactivado' : 'Insumo activado', 
        currentStatus ? 'El insumo ha sido desactivado correctamente' : 'El insumo ha sido activado correctamente'
      );
      
      // Refresh data
      fetchQuoteInputs();
    } catch (error: any) {
      console.error('Error toggling quote input status:', error);
      setError(error.message || 'Error al cambiar el estado del insumo');
      setShowErrorModal(true);
    }
  };
  
  // Add new input type
  const handleAddInputType = async () => {
    if (!newInputTypeName.trim()) {
      setError('El nombre del tipo de insumo es obligatorio');
      setShowErrorModal(true);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('quote_input_types')
        .insert([{ name: newInputTypeName.trim() }])
        .select();
      
      if (error) throw error;
      
      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: user?.id,
          action: 'Creación de tipo de insumo para cotización',
          entity_type: 'quote_input_type',
          entity_id: data[0].id,
          details: `Tipo de insumo creado: ${newInputTypeName.trim()}`,
          action_type: 'create'
        }]);
      
      toast.success('Tipo de insumo creado', 'El tipo de insumo ha sido creado correctamente');
      
      // Refresh data
      fetchQuoteInputTypes();
      
      // Reset form
      setNewInputTypeName('');
      setIsAddingInputType(false);
      
      // Select the new type
      if (data && data.length > 0) {
        setFormState(prev => ({ ...prev, input_type_id: data[0].id }));
      }
    } catch (error: any) {
      console.error('Error creating quote input type:', error);
      setError(error.message || 'Error al crear el tipo de insumo');
      setShowErrorModal(true);
    }
  };
  
  // Delete quote input
  const handleDeleteQuoteInput = (id: string) => {
    setItemToDelete(id);
    setDeleteConfirmOpen(true);
  };

  // Delete input type
  const handleDeleteInputType = async () => {
    if (!inputTypeToDelete) return;
    
    try {
      // Check if there are any inputs using this type
      const { data: usedInputs, error: checkError } = await supabase
        .from('quote_inputs')
        .select('id')
        .eq('input_type_id', inputTypeToDelete)
        .limit(1);
      
      if (checkError) throw checkError;
      
      if (usedInputs && usedInputs.length > 0) {
        setError('No se puede eliminar este tipo de insumo porque está siendo utilizado por uno o más insumos');
        setShowErrorModal(true);
        return;
      }
      
      // Get the input type name for the activity log
      const inputType = inputTypes.find(type => type.id === inputTypeToDelete);
      
      // Delete the input type
      const { error } = await supabase
        .from('quote_input_types')
        .delete()
        .eq('id', inputTypeToDelete);
      
      if (error) throw error;
      
      // Log activity
      if (inputType) {
        await supabase
          .from('activity_logs')
          .insert([{
            user_id: user?.id,
            action: 'Eliminación de tipo de insumo para cotización',
            entity_type: 'quote_input_type',
            entity_id: inputTypeToDelete,
            details: `Tipo de insumo eliminado: ${inputType.name}`,
            action_type: 'delete'
          }]);
      }
      
      toast.success('Tipo de insumo eliminado', 'El tipo de insumo ha sido eliminado correctamente');
      
      // Refresh data
      fetchQuoteInputTypes();
    } catch (error: any) {
      console.error('Error deleting input type:', error);
      setError(error.message || 'Error al eliminar el tipo de insumo');
      setShowErrorModal(true);
    } finally {
      setInputTypeToDelete(null);
      setIsDeleteInputTypeModalOpen(false);
    }
  };

  // Update input type
  const handleUpdateInputType = async () => {
    if (!editingInputTypeId || !editingInputTypeName.trim()) {
      setError('El nombre del tipo de insumo es obligatorio');
      setShowErrorModal(true);
      return;
    }
    
    try {
      const { error } = await supabase
        .from('quote_input_types')
        .update({ name: editingInputTypeName.trim() })
        .eq('id', editingInputTypeId);
      
      if (error) throw error;
      
      // Log activity
      await supabase
        .from('activity_logs')
        .insert([{
          user_id: user?.id,
          action: 'Actualización de tipo de insumo para cotización',
          entity_type: 'quote_input_type',
          entity_id: editingInputTypeId,
          details: `Tipo de insumo actualizado: ${editingInputTypeName.trim()}`,
          action_type: 'update'
        }]);
      
      toast.success('Tipo de insumo actualizado', 'El tipo de insumo ha sido actualizado correctamente');
      
      // Reset form
      setEditingInputTypeName('');
      setEditingInputTypeId(null);
      
      // Refresh data
      fetchQuoteInputTypes();
    } catch (error: any) {
      console.error('Error updating input type:', error);
      setError(error.message || 'Error al actualizar el tipo de insumo');
      setShowErrorModal(true);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Calculator className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Configuración Cotizador
        </h2>
        
        {!isAddingNew && !editingId && !isEditingInputTypes && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              icon={<Settings className="h-5 w-5" />}
              onClick={() => setIsEditingInputTypes(true)}
            >
              Editar Tipos de Insumos
            </Button>
            <Button
              variant="primary"
              icon={<Plus className="h-5 w-5" />}
              onClick={handleAddNew}
            >
              Nuevo Insumo
            </Button>
          </div>
        )}
      </div>
      
      {/* Edit Input Types */}
      {isEditingInputTypes && (
        <Card>
          <CardHeader title="Editar Tipos de Insumos" />
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Tipos de Insumos Existentes</h3>
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setIsAddingInputType(true)}
                >
                  Nuevo Tipo
                </Button>
              </div>
              
              {isAddingInputType && (
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md">
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Input
                        label="Nombre del Tipo"
                        id="newInputTypeName"
                        name="newInputTypeName"
                        value={newInputTypeName}
                        onChange={(e) => setNewInputTypeName(e.target.value)}
                        required
                      />
                    </div>
                    <Button
                      variant="primary"
                      onClick={handleAddInputType}
                      disabled={!newInputTypeName.trim()}
                    >
                      Guardar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsAddingInputType(false);
                        setNewInputTypeName('');
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {inputTypes.map((type) => (
                      <tr key={type.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        {editingInputTypeId === type.id ? (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Input
                              label=""
                              id={`edit-type-${type.id}`}
                              name={`edit-type-${type.id}`}
                              value={editingInputTypeName}
                              onChange={(e) => setEditingInputTypeName(e.target.value)}
                              required
                            />
                          </td>
                        ) : (
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {type.name}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <div className="flex justify-end space-x-2">
                            {editingInputTypeId === type.id ? (
                              <>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={handleUpdateInputType}
                                  disabled={!editingInputTypeName.trim()}
                                >
                                  Guardar
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setEditingInputTypeId(null);
                                    setEditingInputTypeName('');
                                  }}
                                >
                                  Cancelar
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  icon={<Edit className="h-4 w-4" />}
                                  onClick={() => {
                                    setEditingInputTypeId(type.id);
                                    setEditingInputTypeName(type.name);
                                  }}
                                >
                                  Editar
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  icon={<Trash2 className="h-4 w-4" />}
                                  onClick={() => {
                                    setInputTypeToDelete(type.id);
                                    setIsDeleteInputTypeModalOpen(true);
                                  }}
                                >
                                  Eliminar
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {inputTypes.length === 0 && (
                      <tr>
                        <td colSpan={2} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                          No hay tipos de insumos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  icon={<ArrowLeft className="h-4 w-4" />}
                  onClick={() => setIsEditingInputTypes(false)}
                >
                  Volver
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Form for adding/editing */}
      {(isAddingNew || editingId) && !isEditingInputTypes && (
        <Card>
          <CardHeader title={isAddingNew ? "Nuevo Insumo" : "Editar Insumo"} />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo de Insumo
                </label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select
                      label=""
                      value={formState.input_type_id}
                      onChange={(value) => handleInputChange('input_type_id', value)}
                      options={inputTypes.map(type => ({
                        value: type.id,
                        label: type.name
                      }))}
                      required
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddingInputType(true)}
                    icon={<Plus className="h-4 w-4" />}
                    className="h-12"
                  >
                    Nuevo
                  </Button>
                </div>
              </div>
              
              <Input
                label="Nombre del Insumo"
                id="input_name"
                name="input_name"
                value={formState.input_name}
                onChange={(e) => handleInputChange('input_name', e.target.value)}
                required
              />
              
              <Input
                label="Proveedor"
                id="supplier"
                name="supplier"
                value={formState.supplier}
                onChange={(e) => handleInputChange('supplier', e.target.value)}
                required
              />
              
              <Input
                label="Costo Interno"
                id="internal_cost"
                name="internal_cost"
                type="number"
                value={formState.internal_cost.toString()}
                onChange={(e) => handleInputChange('internal_cost', parseFloat(e.target.value) || 0)}
                required
              />
              
              <Input
                label="Markup (%)"
                id="margin"
                name="margin"
                type="number"
                value={formState.margin.toString()}
                onChange={(e) => handleInputChange('margin', parseFloat(e.target.value) || 0)}
                step="1"
                required
              />
              
              {isTelaType(formState.input_type_id) && (
                <Input
                  label="Rinde"
                  id="yield"
                  name="yield"
                  type="number"
                  value={formState.yield?.toString() || ''}
                  onChange={(e) => handleInputChange('yield', e.target.value ? parseFloat(e.target.value) : null)}
                  min="0.01"
                  step="0.01"
                />
              )}
              
              <div className="md:col-span-2">
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Precio Final: {
                      formatCurrency(formState.internal_cost * (1 + formState.margin / 100))
                    }
                  </p>
                  
                  {isTelaType(formState.input_type_id) && formState.yield && formState.yield > 0 && (
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-2">
                      Precio por Metro: {
                        formatCurrency((formState.internal_cost * (1 + formState.margin / 100)) / formState.yield)
                      }
                    </p>
                  )}
                </div>
              </div>
              
              <div className="md:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formState.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                    Activo
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="outline"
                icon={<ArrowLeft className="h-4 w-4" />}
                onClick={handleCancel}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                icon={<Save className="h-4 w-4" />}
                onClick={handleSave}
              >
                Guardar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Filters */}
      {!isAddingNew && !editingId && !isEditingInputTypes && (
        <Card>
          <CardHeader title="Filtros" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="relative">
                <Input
                  label="Buscar"
                  id="searchQuery"
                  name="searchQuery"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por nombre o proveedor..."
                />
                <div className="absolute right-3 top-9">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              
              <Select
                label="Tipo de Insumo"
                value={inputTypeFilter}
                onChange={setInputTypeFilter}
                options={[
                  { value: 'all', label: 'Todos los tipos' },
                  ...uniqueInputTypes.map(type => ({
                    value: type,
                    label: type
                  }))
                ]}
              />
              
              <Select
                label="Proveedor"
                value={supplierFilter}
                onChange={setSupplierFilter}
                options={[
                  { value: 'all', label: 'Todos los proveedores' },
                  ...suppliers.map(supplier => ({
                    value: supplier,
                    label: supplier
                  }))
                ]}
              />
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showInactive"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="showInactive" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Mostrar insumos inactivos
                </label>
              </div>
              
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={handleResetFilters}
              >
                Limpiar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Quote Inputs Table */}
      {!isAddingNew && !editingId && !isEditingInputTypes && (
        <Card>
          <CardHeader title={`Insumos para Cotización (${filteredInputs.length})`} />
          <CardContent>
            {isLoading || isLoadingTypes ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
              </div>
            ) : filteredInputs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No se encontraron insumos con los filtros seleccionados
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Nombre
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Proveedor
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Costo Interno
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Markup (%)
                      </th>
                      {/* Conditional columns based on input type */}
                      {(inputTypeFilter.toLowerCase() === 'tela' || inputTypeFilter === 'all') && (
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Rinde
                        </th>
                      )}
                      {(inputTypeFilter.toLowerCase() === 'tela' || inputTypeFilter === 'all') && (
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Precio/Metro
                        </th>
                      )}
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Precio Final
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredInputs.map((item) => {
                      const typeName = item.quote_input_types ? item.quote_input_types.name : '';
                      const isTela = typeName.toLowerCase() === 'tela';
                      
                      return (
                        <tr key={item.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${!item.is_active ? 'bg-gray-100 dark:bg-gray-800/50' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {typeName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {item.input_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {item.supplier}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                            {formatCurrency(item.internal_cost)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                            {(item.margin * 100).toFixed(0)}%
                          </td>
                          {/* Rinde column (only for Tela or all) */}
                          {(inputTypeFilter.toLowerCase() === 'tela' || inputTypeFilter === 'all') && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                              {isTela && item.yield !== null ? item.yield.toFixed(2) : '-'}
                            </td>
                          )}
                          {/* Price per meter column (only for Tela or all) */}
                          {(inputTypeFilter.toLowerCase() === 'tela' || inputTypeFilter === 'all') && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                              {isTela && item.price_per_meter !== null ? formatCurrency(item.price_per_meter) : '-'}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                            {formatCurrency(item.final_price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                            <button 
                              onClick={() => handleToggleActive(item.id, item.is_active)}
                              className={`inline-flex items-center justify-center p-1 rounded-full ${
                                item.is_active
                                  ? 'text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/20'
                                  : 'text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/20'
                              }`}
                              title={item.is_active ? 'Activo' : 'Inactivo'}
                            >
                              {item.is_active ? (
                                <CheckSquare className="h-5 w-5" />
                              ) : (
                                <Square className="h-5 w-5" />
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Edit className="h-4 w-4" />}
                                onClick={() => handleEdit(item)}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                icon={<Trash2 className="h-4 w-4" />}
                                onClick={() => handleDeleteQuoteInput(item.id)}
                              >
                                Eliminar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Eliminar Insumo"
        message="¿Estás seguro de que deseas eliminar este insumo? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />
      
      {/* Delete Input Type Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteInputTypeModalOpen}
        onClose={() => {
          setIsDeleteInputTypeModalOpen(false);
          setInputTypeToDelete(null);
        }}
        onConfirm={handleDeleteInputType}
        title="Eliminar Tipo de Insumo"
        message="¿Estás seguro de que deseas eliminar este tipo de insumo? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />
      
      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error inesperado'}
      />
      
      {/* Toast Component */}
      {ToastComponent}
    </div>
  );
};

export default QuoteInputs;